import React, { BaseSyntheticEvent } from 'react'
import { IonCol, IonList, IonRow } from '@ionic/react'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { getCcHpiOptionsSelector } from '../../options/selectors/ccHpiOptions.selector'
import { useDispatch, useSelector } from 'react-redux'
import { IOption, IPNNVOption } from '../../../common/common.types'
import { useTranslation } from 'react-i18next'
import {
  setInitialPatientAcuity,
  setAnatomicLocation,
  setConditionAtDestination,
  setFinalPatientAcuity,
  setWorkingDiagnosis,
  setFinalPatientAcuityPNNV,
  setAnatomicLocationPNNV,
  setInitialPatientAcuityPNNV,
} from '../store/diagnosis.actions'
import { getDiagnosisSelector } from '../selectors/getDiagnosis.selector'
import Item from '../../../components/item/Item.component'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import SInput from '../../../components/stringInput/StringInput.component'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { getDiagnosisOptionsSelector } from '../../options/selectors/getDiagnosisOptions.selector'
import { getDatapointsConfigSelector } from '../../configurations/selectors'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { getTripInformationSelector } from '../../tripInformation/selectors/tripInformation.selector'
import Validation from '../../../components/validation/Validation.component'
import { DiagnosisConstants } from '../store/diagnosis.constants'

const Diagnosis: React.FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const serviceConfigurations = useSelector(getServiceConfigSelector)
  const { DEST_CONDITION_OR_FINAL_ACUITY, PR_PT_CONDITION } = serviceConfigurations

  const dpConfigurations = useSelector(getDatapointsConfigSelector)
  const { DP_SYSTEM, DP_SYMPTOMS, DP_IMPRESSION, DP_ANALOC, DP_WORKING_DIAGNOSIS } = dpConfigurations

  const diagnosisState = useSelector(getDiagnosisSelector)
  const {
    initialPatientAcuity,
    initialPatientAcuityPNNV,
    organSystems,
    organSystemsPNNV,
    symptoms,
    symptomsPNNVs,
    impressions,
    impressionsPNNV,
    anatomicLocation,
    anatomicLocationPNNV,
    finalPatientAcuity,
    finalPatientAcuityPNNV,
    conditionAtDestination,
    workingDiagnosis,
  } = diagnosisState

  const ccHpiOptions = useSelector(getCcHpiOptionsSelector)
  const { initialPatientAcuityOptions } = ccHpiOptions

  const diagnosisOptions = useSelector(getDiagnosisOptionsSelector)
  const {
    systemOptions,
    symptomsOptions,
    impressionsOptions,
    anatomicLocationOptions,
    conditionAtDestinationOptions,
    finalPatientAcuityOptions,
  } = diagnosisOptions

  const tripInformationState: any = useSelector(getTripInformationSelector)
  const { patientCategory } = tripInformationState
  const showAnatomicLocation = !!patientCategory?.show_analoc

  const setInitialPatientAcuityOnChange = (option: IOption) => {
    dispatch(setInitialPatientAcuity(option))
  }

  const setInitialPatientAcuityPNNVOnChange = (option: IPNNVOption) => {
    dispatch(setInitialPatientAcuityPNNV(option))
  }

  const setFinalPatientAcuityOnChange = (option: IOption) => {
    dispatch(setFinalPatientAcuity(option))
  }

  const setFinalPatientAcuityPNNVOnChange = (option: IPNNVOption) => {
    dispatch(setFinalPatientAcuityPNNV(option))
  }

  const setConditionAtDestinationOnChange = (option: IOption) => {
    dispatch(setConditionAtDestination(option))
  }

  const onSystemClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.SYSTEM)
  }

  const onSymptomsClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.SYMPTOMS)
  }

  const onImpressionClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.IMPRESSION)
  }

  const setWorkingDiagnosisOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setWorkingDiagnosis(e.detail.value))
  }

  return (
    <>
      <IonList>
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='InitialPatientAcuity'
              options={initialPatientAcuityOptions}
              selectedOption={initialPatientAcuity}
              listHeader={t('ccHpi.initialPatientAcuity')}
              label={t('ccHpi.initialPatientAcuity')}
              action={setInitialPatientAcuityOnChange}
              selectedPNNVOption={initialPatientAcuityPNNV}
              pnnvAction={setInitialPatientAcuityPNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        {(!!DP_SYSTEM || organSystems?.length > 0 || organSystemsPNNV) && systemOptions?.length > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <Item
                  dataTestId='System'
                  label={t('diagnosis.organSystems')}
                  showCheckMark={organSystems?.length > 0 || !!organSystemsPNNV}
                  onClick={onSystemClick}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {(!!DP_SYMPTOMS || symptoms?.length > 0 || symptomsPNNVs?.length > 0) && symptomsOptions?.length > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <Item
                  dataTestId='Symptoms'
                  label={t('diagnosis.symptoms')}
                  showCheckMark={symptoms?.length > 0 || symptomsPNNVs?.length > 0}
                  onClick={onSymptomsClick}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {(!!DP_IMPRESSION || impressions?.length > 0 || impressionsPNNV) && impressionsOptions?.length > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <Item
                  dataTestId='diagnosis'
                  label={t('diagnosis.impression')}
                  showCheckMark={impressions?.length > 0 || !!impressionsPNNV}
                  onClick={onImpressionClick}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}
        {((!!DP_ANALOC && showAnatomicLocation) || anatomicLocation?.length > 0 || anatomicLocationPNNV) && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='AnatomicLocation'
                  listHeader={t('diagnosis.anatomicLocation')}
                  label={t('diagnosis.anatomicLocation')}
                  options={anatomicLocationOptions}
                  savedValues={anatomicLocation || []}
                  action={setAnatomicLocation}
                  pnnvAction={setAnatomicLocationPNNV}
                  savedPNNVValue={anatomicLocationPNNV}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {(!!DP_WORKING_DIAGNOSIS || workingDiagnosis) && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  label={t('diagnosis.workingDiagnosis')}
                  dataTestID='WorkingDiagnosis'
                  inputValue={workingDiagnosis}
                  onChangeAction={setWorkingDiagnosisOnChange}
                />
                <Validation id={`${DiagnosisConstants.SET_WORKING_DIAGNOSIS}`} />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {(PR_PT_CONDITION === 1 ||
          finalPatientAcuity !== null ||
          finalPatientAcuityPNNV !== null ||
          conditionAtDestination !== null) && (
          <>
            {(finalPatientAcuity !== null ||
              finalPatientAcuityPNNV !== null ||
              DEST_CONDITION_OR_FINAL_ACUITY === 1 ||
              DEST_CONDITION_OR_FINAL_ACUITY === 2) && (
              <>
                <IonRow>
                  <IonCol className='picklist'>
                    <SingleSelect
                      dataTestID='FinalPatientAcuity'
                      options={finalPatientAcuityOptions}
                      selectedOption={finalPatientAcuity}
                      listHeader={t('diagnosis.finalPatientAcuity')}
                      label={t('diagnosis.finalPatientAcuity')}
                      action={setFinalPatientAcuityOnChange}
                      pnnvAction={setFinalPatientAcuityPNNVOnChange}
                      selectedPNNVOption={finalPatientAcuityPNNV}
                    />
                  </IonCol>
                </IonRow>

                <hr className='field-divider' />
              </>
            )}

            {(conditionAtDestination !== null ||
              DEST_CONDITION_OR_FINAL_ACUITY === 0 ||
              DEST_CONDITION_OR_FINAL_ACUITY === 2) && (
              <>
                <IonRow>
                  <IonCol className='picklist'>
                    <SingleSelect
                      dataTestID='ConditionAtDestination'
                      options={conditionAtDestinationOptions}
                      selectedOption={conditionAtDestination}
                      listHeader={t('diagnosis.conditionAtDestination')}
                      label={t('diagnosis.conditionAtDestination')}
                      action={setConditionAtDestinationOnChange}
                    />
                  </IonCol>
                </IonRow>

                <hr className='field-divider' />
              </>
            )}
          </>
        )}
      </IonList>
    </>
  )
}

export default Diagnosis
